/**
 * Cached Performance snapshot schedule (CR-4751 — Platform Admin, Helix
 * operators only).
 *
 * When the nightly Cached Performance capture runs on this install, in UTC.
 * It was fixed at 01:00 UTC in code and an edit was undone on every restart;
 * the schedule row is now the only source, so the time can follow this
 * install's daily import — a snapshot taken before the import lands freezes
 * the previous day's figures under today's date.
 */
import { useCallback, useEffect, useState } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import type { DashboardSnapshotSchedule } from '../../../lib/api/platformSettings';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { getErrorMessage } from '../../../hooks/useFetchData';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { AlertCircle, CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function parseIn(value: string, max: number): number | null {
  if (!/^\d{1,2}$/.test(value.trim())) return null;
  const n = Number(value);
  return n <= max ? n : null;
}

export function DashboardSnapshotScheduleSection() {
  const { formatDateTime } = useFormatters();
  const [schedule, setSchedule] = useState<DashboardSnapshotSchedule | null>(null);
  const [hour, setHour] = useState<string>('');
  const [minute, setMinute] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  /** keepTime: a switch toggle must not wipe a time typed but not yet saved. */
  const apply = (s: DashboardSnapshotSchedule, keepTime = false) => {
    setSchedule(s);
    if (keepTime) return;
    setHour(s.hour === null ? '' : String(s.hour));
    setMinute(s.minute === null ? '' : String(s.minute).padStart(2, '0'));
  };

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    platformSettingsAPI.getDashboardSnapshotSchedule()
      .then((r) => apply(r.data))
      .catch((err: unknown) => {
        console.error('Failed to load the Cached Performance schedule:', err);
        setLoadError(getErrorMessage(err, 'Could not load the Cached Performance schedule.'));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (
    patch: Partial<Pick<DashboardSnapshotSchedule, 'enabled' | 'hour' | 'minute'>>, keepTime = false,
  ) => {
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await platformSettingsAPI.updateDashboardSnapshotSchedule(patch);
      apply(data, keepTime);
      toast.success('Cached Performance schedule saved');
    } catch (err: unknown) {
      console.error('Failed to save the Cached Performance schedule:', err);
      const message = getErrorMessage(err, 'Could not save the Cached Performance schedule.');
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (loadError || schedule === null) {
    return (
      <Card>
        <CardContent className="flex items-center justify-between gap-3 py-6">
          <p className="text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {loadError ?? 'Could not load the Cached Performance schedule.'}
          </p>
          <Button size="sm" variant="outline" onClick={load}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const h = parseIn(hour, 23);
  const m = parseIn(minute, 59);
  const changed = h !== schedule.hour || m !== schedule.minute;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Cached Performance Snapshot Schedule
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          When the nightly Cached Performance snapshot is captured on this
          instance, in UTC. Set it after the daily import has landed, so each
          day&apos;s figures are complete when they are frozen.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Switch
            id="cached-capture-enabled"
            checked={schedule.enabled}
            disabled={saving}
            onCheckedChange={(checked) => save({ enabled: checked }, true)}
          />
          <Label htmlFor="cached-capture-enabled">Capture enabled</Label>
        </div>
        {!schedule.exists && (
          <p className="text-xs text-muted-foreground">
            This instance has no capture schedule yet; saving a time creates it.
          </p>
        )}
        {schedule.exists && !schedule.enabled && (
          <p className="text-xs text-amber-700">
            Capture is off: Cached Performance gets no new snapshots until it is switched back on.
          </p>
        )}
        <div className="flex items-end gap-2">
          <div>
            <Label htmlFor="cached-capture-hour">Hour (UTC)</Label>
            <Input
              id="cached-capture-hour" type="number" min={0} max={23} className="w-20"
              value={hour} disabled={saving} onChange={(e) => setHour(e.target.value)}
            />
          </div>
          <span className="pb-2">:</span>
          <div>
            <Label htmlFor="cached-capture-minute">Minute</Label>
            <Input
              id="cached-capture-minute" type="number" min={0} max={59} className="w-20"
              value={minute} disabled={saving} onChange={(e) => setMinute(e.target.value)}
            />
          </div>
          <Button
            disabled={saving || h === null || m === null || !changed}
            onClick={() => h !== null && m !== null && save({ hour: h, minute: m })}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save time'}
          </Button>
        </div>
        {(h === null || m === null) && (
          <p className="text-xs text-red-600">Enter an hour from 0 to 23 and a minute from 0 to 59.</p>
        )}
        {saveError && <p className="text-xs text-red-600">{saveError}</p>}
        <p className="text-xs text-muted-foreground">
          Last run: {schedule.last_run_at ? formatDateTime(schedule.last_run_at) : 'never'}
          {' · '}Runs so far: {schedule.total_run_count}
        </p>
      </CardContent>
    </Card>
  );
}

export default DashboardSnapshotScheduleSection;
